// Frontend translation helper. Calls POST /api/translate which uses
// Emergent universal LLM key on the backend. Returns translations keyed
// by target language code → field key → translated text.
//
// Usage:
//   const tr = await translateFields({ title: "Salam", description: "Necəsən?" });
//   tr.en.title => "Hello"
//   tr.ru.description => "Как дела?"
//
// To keep DB shape clean, use `buildTranslatedDoc(fields, translations)` which
// turns the response into per-language postfixed fields:
//   { title: "Salam", title_en: "Hello", title_ru: "Привет",
//     description: "Necəsən?", description_en: "How are you?", description_ru: "..." }
//
// Plain text only — pass a string for each field. Empty/short fields are skipped server-side.
import axios from "axios";

const BACKEND_URL = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
const ax = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 25000 });

const DEFAULT_TARGETS = ["en", "ru"];

export async function translateFields(fields, { sourceLang = "az", targetLangs = DEFAULT_TARGETS } = {}) {
  // fields: { key1: text1, key2: text2, ... }
  const payload = Object.entries(fields || {})
    .filter(([_, v]) => typeof v === "string" && v.trim().length > 0)
    .map(([key, text]) => ({ key, text }));
  if (payload.length === 0) {
    return Object.fromEntries(targetLangs.map((t) => [t, {}]));
  }
  try {
    const { data } = await ax.post("/translate", {
      source_lang: sourceLang,
      target_langs: targetLangs,
      fields: payload,
    });
    return data?.translations || {};
  } catch (e) {
    console.warn("translateFields failed:", e?.message || e);
    // graceful fallback: echo source values
    return Object.fromEntries(targetLangs.map((t) => [t, Object.fromEntries(payload.map((p) => [p.key, p.text]))]));
  }
}

// Build a flat patch with per-language suffixes for Firestore.
// translations shape: { en: { title: "...", ... }, ru: { ... } }
export function buildTranslatedDoc(baseFields, translations, langs = DEFAULT_TARGETS) {
  const patch = { ...baseFields };
  for (const lang of langs) {
    const block = translations?.[lang] || {};
    for (const key of Object.keys(baseFields)) {
      if (typeof baseFields[key] === "string" && baseFields[key].trim() !== "") {
        patch[`${key}_${lang}`] = block[key] || baseFields[key];
      } else {
        patch[`${key}_${lang}`] = "";
      }
    }
  }
  return patch;
}

// One-shot helper: translate + return merged doc patch.
export async function autoTranslatePatch(fields, opts = {}) {
  const tr = await translateFields(fields, opts);
  return buildTranslatedDoc(fields, tr, opts.targetLangs || DEFAULT_TARGETS);
}
